import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, LogOut } from "lucide-react";

import StudentPortalSignIn from "@/components/StudentPortalSignIn";
import { API_BASE, downloadBlobFile, downloadDataUriFile } from "@/lib/api";
import { captureStudentTokenFromUrl, clearStudentSession, getStudentSession } from "@/lib/studentAuth";

const LOGO = "/mu-logo.png";

function portalErrorFromParams() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("error");
  if (!code) return null;

  if (code === "not_registered") {
    const signedIn = params.get("signed_in") || "your account";
    return `No convocation registration was found for ${signedIn}. Please sign in with the email linked to your registration.`;
  }
  if (code === "no_email") {
    return "We could not read an email address from your Microsoft account. Please try again.";
  }
  return "Sign-in failed. Please try again.";
}

function qrDownloadFilename(htno, q) {
  const base = (htno || "pass").replace(/[^\w.-]+/g, "_");
  if (q.qr_type === "STUDENT") return `${base}_student.png`;
  return `${base}_guest${q.guest_index || ""}.png`;
}

async function downloadPortalQr(q, htno) {
  const filename = qrDownloadFilename(htno, q);
  const session = getStudentSession();

  if (q.id && session?.token) {
    const response = await fetch(`${API_BASE}/public/student/qrs/${q.id}/download`, {
      headers: { Authorization: `Bearer ${session.token}` },
    });
    if (response.ok) {
      const blob = await response.blob();
      downloadBlobFile(blob, filename);
      return;
    }
  }

  if (q.image) {
    await downloadDataUriFile(q.image, filename);
    return;
  }

  throw new Error("This QR pass is not available to download yet.");
}

export default function StudentPortal() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [authRequired, setAuthRequired] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);

  const fetchPortal = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAuthRequired(null);

    const session = getStudentSession();
    const headers = {};
    if (session?.token) {
      headers.Authorization = `Bearer ${session.token}`;
    }

    try {
      const r = await fetch(`${API_BASE}/public/student`, { headers });
      const body = await r.json().catch(() => ({}));
      if (r.status === 401) {
        setAuthRequired(body);
        setData(null);
        return;
      }
      if (!r.ok) {
        throw new Error(body.detail || "Unable to load your convocation details");
      }
      setData(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    captureStudentTokenFromUrl();
    const paramError = portalErrorFromParams();
    if (paramError) {
      setError(paramError);
      setLoading(false);
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    fetchPortal();
  }, [fetchPortal]);

  const qrKey = (q) => q.id || `${q.qr_type}-${q.guest_index || 0}`;

  const handleDownload = async (q) => {
    if (!data) return;
    const key = qrKey(q);
    setDownloadingKey(key);
    try {
      await downloadPortalQr(q, data.htno);
    } catch (e) {
      setError(e.message || "Download failed. Please try again.");
    } finally {
      setDownloadingKey(null);
    }
  };

  const handleDownloadAll = async () => {
    if (!data?.qrs?.length) return;
    setDownloadingAll(true);
    setError(null);
    try {
      for (const q of data.qrs) {
        await downloadPortalQr(q, data.htno);
        await new Promise((resolve) => setTimeout(resolve, 350));
      }
    } catch (e) {
      setError(e.message || "Download failed. Please try again.");
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleLogout = () => {
    clearStudentSession();
    setData(null);
    setError(null);
    fetchPortal();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-[#B60F2A]" />
        <p className="text-[#6B7280] text-sm">Loading your convocation details…</p>
      </div>
    );
  }

  if (authRequired || (error && !data)) {
    return (
      <StudentPortalSignIn
        error={error}
        authRequired={authRequired}
        onSignedIn={fetchPortal}
      />
    );
  }

  if (!data) return null;

  const eventLabel = [data.event_name, data.event_year].filter(Boolean).join(" ") || "Convocation 2026";
  const qrs = data.qrs || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#A6192E]/10 to-white">
      <div className="max-w-lg mx-auto px-6 py-10">
        <div className="flex items-center justify-between gap-3 mb-8">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="MU" className="h-10 w-auto" />
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-500">Convocation</p>
              <p className="font-semibold text-slate-800">{eventLabel}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {data.signed_in_email && (
              <p className="text-[10px] text-slate-500 text-right max-w-[160px] truncate" title={data.signed_in_email}>
                {data.signed_in_email}
              </p>
            )}
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              data-testid="portal-logout-btn"
            >
              <LogOut size={14} />
              Log out
            </button>
          </div>
        </div>

        <div className="bg-white border border-border rounded-xl p-6 shadow-sm mb-6">
          <p className="text-xs uppercase tracking-widest text-slate-500">Graduate</p>
          <h1 className="font-serif text-3xl text-slate-900 mt-1">{data.student_name}</h1>
          <p className="text-slate-500 mt-1">{data.htno}</p>
          {(data.entity || data.program) && (
            <p className="text-sm text-slate-600 mt-3">
              {[data.entity, data.program].filter(Boolean).join(" · ")}
            </p>
          )}
          {data.specialization && (
            <p className="text-sm text-slate-500 mt-1">{data.specialization}</p>
          )}
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {qrs.length > 0 ? (
          <section>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <p className="text-sm text-slate-600">
                Download each QR pass as a PNG and carry them for check-in — guests need their own QR for entry.
              </p>
              {qrs.length > 1 && (
                <button
                  type="button"
                  onClick={handleDownloadAll}
                  disabled={downloadingAll || Boolean(downloadingKey)}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#A6192E] px-4 py-2 text-sm font-medium text-white hover:bg-[#8f0c24] disabled:opacity-60"
                  data-testid="download-all-qrs-btn"
                >
                  {downloadingAll ? (
                    <><Loader2 size={16} className="animate-spin" /> Downloading…</>
                  ) : (
                    <><Download size={16} /> Download all</>
                  )}
                </button>
              )}
            </div>
            <div className="grid gap-4">
              {qrs.map((q) => {
                const label = q.caption || (q.qr_type === "STUDENT" ? data.student_name : `Guest ${q.guest_index || ""}`);
                const key = qrKey(q);
                const isDownloading = downloadingKey === key;
                const canDownload = Boolean(q.image || q.id);
                return (
                  <div
                    key={key}
                    className="bg-white border border-border rounded-xl p-5 text-center shadow-sm"
                    data-testid={`portal-qr-${q.qr_type === "STUDENT" ? "student" : `guest-${q.guest_index}`}`}
                  >
                    <p className="text-sm font-semibold text-slate-800 mb-4">{label}</p>
                    {q.image && (
                      <button
                        type="button"
                        onClick={() => handleDownload(q)}
                        disabled={isDownloading || downloadingAll}
                        className="mx-auto block rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A6192E]/40 disabled:opacity-60"
                        aria-label={`Download ${label} QR pass`}
                        data-testid={`portal-qr-image-${q.qr_type === "STUDENT" ? "student" : `guest-${q.guest_index}`}`}
                      >
                        <img
                          src={q.image}
                          alt={label}
                          className="mx-auto w-full max-w-[240px]"
                        />
                        <span className="mt-2 block text-xs text-slate-500">Tap QR to download</span>
                      </button>
                    )}
                    {canDownload && (
                      <button
                        type="button"
                        onClick={() => handleDownload(q)}
                        disabled={isDownloading || downloadingAll}
                        className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg border border-[#A6192E] px-4 py-2 text-sm font-medium text-[#A6192E] hover:bg-[#A6192E]/5 disabled:opacity-60"
                        data-testid={`download-qr-${q.qr_type === "STUDENT" ? "student" : `guest-${q.guest_index}`}`}
                      >
                        {isDownloading ? (
                          <><Loader2 size={16} className="animate-spin" /> Downloading…</>
                        ) : (
                          <><Download size={16} /> Download PNG</>
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ) : (
          <div className="bg-white border border-dashed border-border rounded-xl p-6 text-center text-sm text-slate-500">
            QR passes are not available yet. Please check back later or contact the convocation team.
          </div>
        )}
      </div>
    </div>
  );
}
